import React from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import DateWidget from '~/components/Common/Elements/DateWidget';

import { useGetProjectTemplates } from '~/hooks/apis/contracting/useProjectTemplates';

import type { NewProjectValues } from '~/services/ContractingService';

const REQUIRED_MESSAGE = 'Complete this field.';
const MIN_ESTIMATED_HOURS = 1;
const EMPTY_VALUES = { templateId: '', estimatedHours: '', startDate: '', endDate: '' };

type FormValues = typeof EMPTY_VALUES;

interface Props {
  currentProjectName?: string;
  isSaving: boolean;
  open: boolean;
  onClose: () => void;
  onCreate: (values: NewProjectValues) => Promise<void>;
}

export default function CreateProjectDialog({ currentProjectName, isSaving, open, onClose, onCreate }: Props) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
  } = useForm<FormValues>({ defaultValues: EMPTY_VALUES });
  const {
    data: templates = [],
    error: templatesError,
    isLoading: isLoadingTemplates,
  } = useGetProjectTemplates({
    enabled: open,
  });

  const handleSave = handleSubmit(async ({ estimatedHours, ...values }) => {
    try {
      await onCreate({ ...values, estimatedHours: Number(estimatedHours) });
    } catch (error) {
      setError('root', { message: error instanceof Error ? error.message : 'Could not create the project.' });
    }
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={isSaving ? undefined : onClose}
      TransitionProps={{ onExited: () => reset() }}
    >
      <DialogTitle>New project</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
          {templatesError && <Alert severity="error">Could not load project templates: {templatesError.message}</Alert>}
          {currentProjectName && (
            <Alert severity="warning">
              This replaces {currentProjectName} with a blank project. Its task statuses, order and assignees will be
              lost.
            </Alert>
          )}
          <Controller
            name="templateId"
            control={control}
            rules={{ required: REQUIRED_MESSAGE }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                select
                disabled={isLoadingTemplates}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                label="Project template"
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="estimatedHours"
            control={control}
            rules={{
              required: REQUIRED_MESSAGE,
              validate: (value) =>
                (!Number.isInteger(Number(value)) && 'Enter a whole number of hours.') ||
                (Number(value) < MIN_ESTIMATED_HOURS && `Enter at least ${MIN_ESTIMATED_HOURS} hour.`) ||
                true,
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                label="Estimated hours"
                type="number"
                slotProps={{ htmlInput: { min: MIN_ESTIMATED_HOURS, step: 1 } }}
              />
            )}
          />
          <Controller
            name="startDate"
            control={control}
            rules={{ required: REQUIRED_MESSAGE }}
            render={({ field, fieldState }) => (
              <DateWidget
                fullWidth
                label="Project start date"
                value={field.value}
                onChange={field.onChange}
                slotProps={{
                  textField: { required: true, error: !!fieldState.error, helperText: fieldState.error?.message },
                }}
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            rules={{
              validate: (value, { startDate }) =>
                (!!value && value >= startDate) || 'Enter a date on or after the start date.',
            }}
            render={({ field, fieldState }) => (
              <DateWidget
                fullWidth
                label="Planned end date"
                value={field.value}
                onChange={field.onChange}
                slotProps={{
                  textField: { required: true, error: !!fieldState.error, helperText: fieldState.error?.message },
                }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSaving} variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSaving} variant="contained" onClick={handleSave}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
